import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { listBrandingProfiles, WEKIT_DEFAULTS, type BrandingProfile } from "@/lib/demoBranding";
import { Play, Plus, Check, AlertCircle, Sparkles, Loader2 } from "lucide-react";

const EMPTY: Partial<BrandingProfile> = {
  slug: "",
  school_name: "",
  principal_name: "",
  academic_year: "2026-27",
  tagline: WEKIT_DEFAULTS.tagline,
  logo_url: "",
  primary_color: WEKIT_DEFAULTS.primary,
  secondary_color: WEKIT_DEFAULTS.secondary,
  accent_color: WEKIT_DEFAULTS.accent,
  is_active: false,
};

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function validate(p: Partial<BrandingProfile>): Record<string, string> {
  const e: Record<string, string> = {};
  const name = (p.school_name ?? "").trim();
  if (!name) e.school_name = "School name is required — appears as the hero title.";
  else if (name.length < 2) e.school_name = "Must be at least 2 characters.";
  else if (name.length > 80) e.school_name = "Keep under 80 characters so it fits the cinematic hero.";

  const slug = (p.slug ?? "").trim();
  if (!slug) e.slug = "Slug is required — used as the profile identifier.";
  else if (!SLUG.test(slug)) e.slug = "Lowercase letters, numbers and single dashes only (e.g. greenfield-intl).";
  else if (slug.length > 40) e.slug = "Keep slug under 40 characters.";

  if (p.principal_name && p.principal_name.length > 60) e.principal_name = "Keep under 60 characters.";
  if (p.academic_year && !/^\d{4}(-\d{2,4})?$/.test(p.academic_year)) e.academic_year = "Use format 2026 or 2026-27.";
  if (p.tagline && p.tagline.length > 140) e.tagline = "Keep tagline under 140 characters.";

  if (p.logo_url) {
    try {
      const u = new URL(p.logo_url);
      if (!["http:", "https:"].includes(u.protocol)) e.logo_url = "Logo URL must start with https://";
    } catch { e.logo_url = "Enter a valid URL (https://…) or leave blank for the initial fallback."; }
  }

  for (const k of ["primary_color", "secondary_color", "accent_color"] as const) {
    const v = (p[k] ?? "").trim();
    if (!v) e[k] = "Required — drives cinematic gradients.";
    else if (!HEX.test(v)) e[k] = "Use a hex value like #00665B.";
  }
  return e;
}

export default function AdminDemoBranding() {
  const [profiles, setProfiles] = useState<BrandingProfile[]>([]);
  const [editing, setEditing] = useState<Partial<BrandingProfile>>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [autofillUrl, setAutofillUrl] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [autofillError, setAutofillError] = useState<string | null>(null);
  const [scrapePreview, setScrapePreview] = useState<{
    source_url?: string;
    logo_url?: string;
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    school_name?: string;
    tagline?: string;
  } | null>(null);
  const [logoPreviewOk, setLogoPreviewOk] = useState(true);

  const normalizeInstitutionUrl = (raw: string): { url: string | null; error?: string } => {
    const trimmed = raw.trim();
    if (!trimmed) return { url: null, error: "Enter the institution's website URL." };
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const u = new URL(withScheme);
      if (!["http:", "https:"].includes(u.protocol)) return { url: null, error: "Only http:// or https:// URLs are allowed." };
      if (!u.hostname.includes(".")) return { url: null, error: "That doesn't look like a valid domain." };
      u.hash = "";
      return { url: u.toString() };
    } catch {
      return { url: null, error: "Enter a valid URL (e.g. https://school.edu)." };
    }
  };

  const runAutofill = async () => {
    setAutofillError(null);
    const { url, error: urlErr } = normalizeInstitutionUrl(autofillUrl);
    if (!url) {
      setAutofillError(urlErr ?? "Invalid URL.");
      return;
    }
    setAutofillUrl(url);
    setAutofilling(true);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-institution-branding", {
        body: { url },
      });
      if (error) throw new Error(error.message || "Edge function failed");
      if (!data?.ok) throw new Error(data?.error || "Scrape failed");

      const patch: Partial<BrandingProfile> = {};
      if (data.logo_url) patch.logo_url = data.logo_url;
      if (data.primary_color) patch.primary_color = data.primary_color;
      if (data.secondary_color) patch.secondary_color = data.secondary_color;
      if (data.accent_color) patch.accent_color = data.accent_color;
      if (data.school_name && !editing.school_name) patch.school_name = data.school_name;
      if (data.tagline && !editing.tagline) patch.tagline = data.tagline;

      // Derive a URL-safe slug from the school name (only if the slug field is empty,
      // so we never clobber an admin's manual override).
      const nameForSlug = patch.school_name ?? editing.school_name ?? data.school_name;
      if (nameForSlug && !editing.slug) {
        const derived = String(nameForSlug)
          .toLowerCase()
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 40);
        if (derived) patch.slug = derived;
      }

      if (Object.keys(patch).length === 0) {
        setAutofillError("Firecrawl couldn't extract branding for that URL. Your form values are unchanged — fill fields manually or try a deeper page (e.g. the About page).");
        setScrapePreview(null);
        return;
      }

      setScrapePreview({
        source_url: data.source_url ?? url,
        logo_url: data.logo_url,
        primary_color: data.primary_color,
        secondary_color: data.secondary_color,
        accent_color: data.accent_color,
        school_name: data.school_name,
        tagline: data.tagline,
      });
      setLogoPreviewOk(true);
      update(patch);
      toast({ title: "Branding imported", description: "Review the preview and adjust before saving." });
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setAutofillError(`Auto-fill failed: ${msg}. Your form values were not changed.`);
    } finally {
      setAutofilling(false);
    }
  };



  const load = async () => setProfiles(await listBrandingProfiles());
  useEffect(() => { load(); }, []);

  const update = (patch: Partial<BrandingProfile>) => {
    const next = { ...editing, ...patch };
    setEditing(next);
    if (Object.keys(errors).length) setErrors(validate(next));
  };

  const save = async () => {
    const errs = validate(editing);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast({ title: "Please fix the highlighted fields", description: "Incomplete profiles would break the cinematic welcome.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      const { error } = editing.id
        ? await (supabase as any).from("demo_branding_profiles").update(payload).eq("id", editing.id)
        : await (supabase as any).from("demo_branding_profiles").insert(payload);
      if (error) throw error;
      toast({ title: "Saved" });
      setEditing(EMPTY);
      setErrors({});
      await load();
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id: string) => {
    await (supabase as any).from("demo_branding_profiles").update({ is_active: true }).eq("id", id);
    toast({ title: "Profile activated" });
    load();
  };

  return (
    <>
      <PageHead title="Demo Branding | WeKIT Admin" description="Meeting Mode branding profiles" path="/admin/demo-institution" />
      <div className="container py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold">Demo Branding™</h1>
            <p className="text-muted-foreground text-sm">Customize the cinematic welcome for each institution demo.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditing(EMPTY); setErrors({}); }}><Plus className="w-4 h-4 mr-2" />New profile</Button>
            <Link to="/meeting"><Button><Play className="w-4 h-4 mr-2" />Start Meeting</Button></Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle>{editing.id ? "Edit profile" : "New profile"}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <fieldset disabled={autofilling} className="space-y-4 disabled:opacity-60">
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" /> Auto-fill from institution website
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    inputMode="url"
                    value={autofillUrl}
                    onChange={(e) => { setAutofillUrl(e.target.value); if (autofillError) setAutofillError(null); }}
                    placeholder="e.g. baldwininternationalschool.net"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runAutofill(); } }}
                    aria-invalid={!!autofillError}
                    aria-describedby={autofillError ? "autofill-error" : undefined}
                  />
                  <Button type="button" onClick={runAutofill} disabled={autofilling}>
                    {autofilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    <span className="ml-1.5">{autofilling ? "Scraping…" : "Import"}</span>
                  </Button>
                </div>
                {autofilling && (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" role="status" aria-live="polite">
                    <Loader2 className="w-3 h-3 animate-spin" /> Contacting Firecrawl and extracting brand identity…
                  </p>
                )}
                {autofillError && (
                  <p id="autofill-error" role="alert" className="text-[11px] text-destructive flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 mt-[1px] shrink-0" />
                    <span>{autofillError}</span>
                  </p>
                )}
                {!autofilling && !autofillError && (
                  <p className="text-[11px] text-muted-foreground">
                    Pulls the school's logo and brand colors via Firecrawl. Review before saving.
                  </p>
                )}

                {scrapePreview && (
                  <div className="mt-3 rounded-md border bg-background p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Scrape preview</span>
                      {scrapePreview.source_url && (
                        <a href={scrapePreview.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline truncate max-w-[60%]">
                          {scrapePreview.source_url}
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-md border bg-muted/30 flex items-center justify-center overflow-hidden shrink-0">
                        {scrapePreview.logo_url && logoPreviewOk ? (
                          <img
                            src={scrapePreview.logo_url}
                            alt="Scraped logo"
                            className="max-w-full max-h-full object-contain"
                            onError={() => setLogoPreviewOk(false)}
                          />
                        ) : (
                          <span className="text-[10px] text-muted-foreground text-center px-1">
                            {scrapePreview.logo_url ? "Logo blocked" : "No logo"}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        {scrapePreview.school_name && (
                          <div className="text-sm font-medium truncate">{scrapePreview.school_name}</div>
                        )}
                        {scrapePreview.tagline && (
                          <div className="text-[11px] text-muted-foreground line-clamp-2">{scrapePreview.tagline}</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {(["primary_color", "secondary_color", "accent_color"] as const).map((k) => {
                        const label = k === "primary_color" ? "Primary" : k === "secondary_color" ? "Secondary" : "Accent";
                        const v = scrapePreview[k];
                        return (
                          <div key={k} className="rounded-md border p-2 flex flex-col items-center gap-1">
                            <div
                              className="w-full h-8 rounded"
                              style={{ background: v || "transparent", backgroundImage: v ? undefined : "repeating-linear-gradient(45deg,hsl(var(--muted)),hsl(var(--muted)) 4px,transparent 4px,transparent 8px)" }}
                              aria-label={`${label} color swatch`}
                            />
                            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
                            <span className="text-[10px] font-mono">{v ?? "—"}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div
                      className="rounded-md p-3 text-xs"
                      style={{
                        background: `linear-gradient(135deg, ${scrapePreview.primary_color ?? "#00665B"}, ${scrapePreview.secondary_color ?? "#0818A8"})`,
                        color: "#fff",
                      }}
                    >
                      <div className="opacity-80 text-[10px] uppercase tracking-wider">Hero preview</div>
                      <div className="font-semibold">{scrapePreview.school_name ?? editing.school_name ?? "School name"}</div>
                      <button
                        type="button"
                        className="mt-1 inline-block rounded px-2 py-0.5 text-[10px] font-medium"
                        style={{ background: scrapePreview.accent_color ?? "#FF5F1F", color: "#180205" }}
                      >
                        Call to action
                      </button>
                    </div>
                  </div>
                )}
              </div>


              <Field label="School name *" error={errors.school_name}>
                <Input value={editing.school_name ?? ""} onChange={(e) => update({ school_name: e.target.value })} aria-invalid={!!errors.school_name} />
              </Field>
              <Field label="Slug * (url-safe id)" error={errors.slug}>
                <Input value={editing.slug ?? ""} onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} aria-invalid={!!errors.slug} />
              </Field>
              <Field label="Principal name" error={errors.principal_name}>
                <Input value={editing.principal_name ?? ""} onChange={(e) => update({ principal_name: e.target.value })} placeholder="e.g. Sister Maria" />
              </Field>
              <Field label="Academic year" error={errors.academic_year}>
                <Input value={editing.academic_year ?? ""} onChange={(e) => update({ academic_year: e.target.value })} placeholder="2026-27" />
              </Field>
              <Field label="Tagline" error={errors.tagline}>
                <Textarea rows={2} value={editing.tagline ?? ""} onChange={(e) => update({ tagline: e.target.value })} />
              </Field>
              <Field label="Logo URL" error={errors.logo_url}>
                <Input value={editing.logo_url ?? ""} onChange={(e) => update({ logo_url: e.target.value })} placeholder="https://…" />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <ColorField label="Primary" value={editing.primary_color ?? ""} onChange={(v) => update({ primary_color: v })} error={errors.primary_color} />
                <ColorField label="Secondary" value={editing.secondary_color ?? ""} onChange={(v) => update({ secondary_color: v })} error={errors.secondary_color} />
                <ColorField label="Accent" value={editing.accent_color ?? ""} onChange={(v) => update({ accent_color: v })} error={errors.accent_color} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                {editing.id && <Button variant="outline" onClick={() => { setEditing(EMPTY); setErrors({}); }}>Cancel</Button>}
              </div>
              </fieldset>
            </CardContent>

          </Card>

          <Card>
            <CardHeader><CardTitle>Live preview</CardTitle></CardHeader>
            <CardContent>
              <div
                className="rounded-2xl p-8 text-center min-h-[280px] flex flex-col items-center justify-center"
                style={{
                  background: `radial-gradient(ellipse at 30% 20%, ${editing.primary_color}33, transparent 60%), radial-gradient(ellipse at 70% 80%, ${editing.secondary_color}33, transparent 60%), #0b0f1a`,
                }}
              >
                {editing.logo_url ? (
                  <img src={editing.logo_url} alt="" className="h-16 mb-4" />
                ) : (
                  <div className="h-16 w-16 rounded-xl mb-4 flex items-center justify-center text-2xl font-bold text-white"
                       style={{ background: `linear-gradient(135deg, ${editing.primary_color}, ${editing.secondary_color})` }}>
                    {(editing.school_name || "W").slice(0, 1)}
                  </div>
                )}
                <p className="uppercase tracking-widest text-xs mb-2" style={{ color: editing.accent_color }}>
                  {editing.academic_year ? `AY ${editing.academic_year}` : "Academic Year"}
                </p>
                <h2 className="text-2xl font-bold text-white mb-2">{editing.school_name || "School name"}</h2>
                {editing.principal_name && <p className="text-white/80">Welcome, {editing.principal_name}</p>}
                <p className="text-white/50 text-sm mt-2">{editing.tagline}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle>Saved profiles</CardTitle></CardHeader>
          <CardContent>
            {profiles.length === 0 ? (
              <p className="text-sm text-muted-foreground">No profiles yet.</p>
            ) : (
              <div className="divide-y">
                {profiles.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-3">
                    <div className="flex gap-1">
                      <span className="w-4 h-4 rounded-full" style={{ background: p.primary_color ?? undefined }} />
                      <span className="w-4 h-4 rounded-full" style={{ background: p.secondary_color ?? undefined }} />
                      <span className="w-4 h-4 rounded-full" style={{ background: p.accent_color ?? undefined }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{p.school_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.slug} · {p.principal_name || "—"}</p>
                    </div>
                    {p.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600"><Check className="w-3 h-3" />Active</span>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => activate(p.id)}>Set active</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => { setEditing(p); setErrors({}); }}>Edit</Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>
      )}
    </div>
  );
}
function ColorField({ label, value, onChange, error }: { label: string; value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-1">
        <input type="color" value={HEX.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-9 rounded border cursor-pointer" />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="text-xs" aria-invalid={!!error} />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
